import type { PayloadAction } from '@reduxjs/toolkit';
import { createSelector, createSlice } from '@reduxjs/toolkit';
import type { FieldValues, UseFormSetValue } from 'react-hook-form';
import type {
  PayGroupCategories,
  PayHubAdminPanelDomainCurrenciesEnumsCashierMethodParamExtendedType,
  PayHubCashierContractsCashierSettingsResponse,
  PayHubCashierContractsParamOptionResponse,
  PayHubCashierContractsTaxResponse,
  PayHubWidgetContractsPayoutsSelectPayoutGroupResponse,
  PayoutGroupCategories,
} from 'store/models/APIModels';
import { extendedWithdrawalAPI } from 'store/services/withdrawalAPI';

import { getCachedGroupAttributes } from '../../utils';
import { DEFAULT_EMPTY_CURRENCY_ALPHA3, DEFAULT_EMPTY_CURRENCY_PRECISION } from '../constants';
import type { UIPayout, UIPayoutGroups, UIWithdrawalCard, VerificationData } from '../formatters';

interface WithdrawalSlice extends UIPayout {
  currentMethod?: UIPayoutGroups;
  extendedTypeForCardParam?: PayHubAdminPanelDomainCurrenciesEnumsCashierMethodParamExtendedType;
  responceByReqId?: Record<string, PayHubWidgetContractsPayoutsSelectPayoutGroupResponse>;
  requestGroupIds?: Record<string, string | null>;
  selectedBetShop?: PayHubCashierContractsParamOptionResponse;
  betShopOptions?: PayHubCashierContractsParamOptionResponse[];
  widgetSettings: PayHubCashierContractsCashierSettingsResponse;
  isNewPamAccount?: boolean;
  betShopSetValueCallback?: UseFormSetValue<FieldValues>;
  betShopFieldName?: string;
  paygroupCategories?: PayGroupCategories;
  withdrawalMethods?: UIPayoutGroups[];
}

const initialState: WithdrawalSlice = {
  methodsTypes: [],
  isReady: false,
  canAutoRedirect: false,
  isRequestPending: false,
  submitPaymentReady: false,
  currencyId: undefined,
  currencyAlpha3: undefined,
  widgetSettings: {},
  methods: [],
  carouselMethodList: [],
  growthbookFeatures: [],
  viewData: [],
  cards: [],
  selectedCard: {},
  accountVerifBlocked: false,
  accountVerifBlockedUntil: null,
  verifyNewPayoutAccount: false,
  tax: {},
  verificationData: {},
  userBalance: 0,
  responceByReqId: {},
  requestGroupIds: {},
  selectedBetShop: {},
  betShopOptions: [],
  authToken: '',
  merchantApiKey: '',
  isNewPamAccount: false,
  betShopFieldName: '',
  paygroupCategories: undefined,
  withdrawalMethods: [],
  walletCurrencies: [],
  shouldSkipInitFeature: false,
  selectedCurrency: null,
  userCurrency: null,
  userBlocked: false,
  defaultFiatCurrency: null,
  meshMethods: [],
};

type accountVerifBlocked = {
  accountVerifBlocked: boolean;
  accountVerifBlockedUntil: string | null;
};

export const withdrawalSlice = createSlice({
  name: 'withdrawal',
  initialState,
  reducers: {
    setWidgetSettings(state, action: PayloadAction<PayHubCashierContractsCashierSettingsResponse>) {
      return {
        ...state,
        widgetSettings: action.payload,
      };
    },
    setCurrentMethod(state, action: PayloadAction<UIPayoutGroups>) {
      return {
        ...state,
        currentMethod: action.payload,
      };
    },
    setWithdrawalCardsList(state, action: PayloadAction<UIWithdrawalCard[]>) {
      return {
        ...state,
        cards: action.payload,
      };
    },
    setSelectedWithdrawalCard: (state, action: PayloadAction<UIWithdrawalCard>) => ({
      ...state,
      selectedCard: action.payload,
    }),
    disableAccountVerification: (state, action: PayloadAction<accountVerifBlocked>) => ({
      ...state,
      accountVerifBlocked: action.payload.accountVerifBlocked,
      accountVerifBlockedUntil: action.payload.accountVerifBlockedUntil,
    }),
    setAccVarificationData: (state, action: PayloadAction<VerificationData>) => ({
      ...state,
      verificationData: action.payload,
    }),
    setWithdrawalTax: (state, action: PayloadAction<PayHubCashierContractsTaxResponse | undefined>) => ({
      ...state,
      tax: action.payload,
    }),
    cleanCurrentMethod: (state) => ({
      ...state,
      currentMethod: undefined,
    }),
    setVerifyNewPayoutAccount: (state, action: PayloadAction<boolean>) => ({
      ...state,
      verifyNewPayoutAccount: action.payload,
    }),
    setExtendedTypeForCardParam: (
      state,
      action: PayloadAction<PayHubAdminPanelDomainCurrenciesEnumsCashierMethodParamExtendedType>,
    ) => ({
      ...state,
      extendedTypeForCardParam: action.payload,
    }),
    setBetShopSelected: (state, action: PayloadAction<PayHubCashierContractsParamOptionResponse>) => ({
      ...state,
      selectedBetShop: action.payload,
    }),
    setBetShopOptions: (state, action: PayloadAction<PayHubCashierContractsParamOptionResponse[]>) => ({
      ...state,
      betShopOptions: action.payload,
    }),
    setIsNewPamAccount: (state, action: PayloadAction<boolean>) => ({
      ...state,
      isNewPamAccount: action.payload,
    }),
    setBetShopSetValueCallback: (state, action: PayloadAction<UseFormSetValue<FieldValues> | undefined>) => ({
      ...state,
      betShopSetValueCallback: action.payload,
    }),
    setBetShopFieldName: (state, action: PayloadAction<string>) => ({
      ...state,
      betShopFieldName: action.payload,
    }),
    setPayoutGroupCategories: (state, action: PayloadAction<PayoutGroupCategories | undefined>) => {
      state.paygroupCategories = action.payload;
    },
    setWithdrawalMethods: (state, action: PayloadAction<UIPayoutGroups[]>) => {
      state.withdrawalMethods = action.payload;
    },
    setShouldSkipInitFeature: (state, action: PayloadAction<boolean>) => {
      state.shouldSkipInitFeature = action.payload;
    },
    setAmountStepConfig: (
      state,
      action: PayloadAction<{ amountCanBeChanged: boolean; possibleChangesAmount: number }>,
    ) => {
      if (state.currentMethod) {
        state.currentMethod.amountCanBeChanged = action.payload.amountCanBeChanged;
        state.currentMethod.possibleChangesAmount = action.payload.possibleChangesAmount;
      }
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(extendedWithdrawalAPI.endpoints.getWithdrawalMethodInfo.matchPending, (state, action) => {
      const { payGroupId } = action.meta.arg.originalArgs;
      if (state.requestGroupIds) {
        state.requestGroupIds[payGroupId.toString()] = action.meta.requestId;
      }
    });
    builder.addMatcher(extendedWithdrawalAPI.endpoints.getWithdrawalMethodInfo.matchFulfilled, (state, action) => {
      const { payGroupId } = action.meta.arg.originalArgs;
      const requestId = state.requestGroupIds ? state.requestGroupIds[payGroupId] : null;
      if (requestId === action.meta.requestId && state.responceByReqId) {
        state.responceByReqId[action.meta.requestId] = action.payload;
      }
      const methodInfo = action.payload as unknown as { amountCanBeChanged?: boolean; possibleChangesAmount?: number };
      if (state.currentMethod) {
        state.currentMethod.amountCanBeChanged = methodInfo?.amountCanBeChanged || false;
        state.currentMethod.possibleChangesAmount = methodInfo?.possibleChangesAmount || 0;
      }
    });
  },
});

export const withdrawalCardsSelector = ({ withdrawal }: { withdrawal: WithdrawalSlice }) => withdrawal.cards || [];
export const withdrawalSelectedCardSelector = createSelector(
  ({ withdrawal }: { withdrawal: WithdrawalSlice }) => withdrawal.selectedCard,
  (selectedCard) => selectedCard || {},
);
export const withdrawalTaxSelector = createSelector(
  (state: { withdrawal?: WithdrawalSlice }) => state.withdrawal,
  (withdrawal) => (withdrawal ? withdrawal.tax || {} : {}),
);
export const isAccVerifBlockedSelector = ({ withdrawal }: { withdrawal: UIPayout }) => withdrawal.accountVerifBlocked;
export const accountVerifBlockedUntilSelector = ({ withdrawal }: { withdrawal: UIPayout }) =>
  withdrawal.accountVerifBlockedUntil;
export const userVerificationDataSelector = ({ withdrawal }: { withdrawal: UIPayout }) => withdrawal.verificationData;
export const verifyNewPayoutAccountSelector = ({ withdrawal }: { withdrawal: UIPayout }) =>
  withdrawal.verifyNewPayoutAccount;

export const isNewPamAccountSelector = (state: { withdrawal?: WithdrawalSlice }) =>
  state.withdrawal?.isNewPamAccount || false;

export const currentWithdrawalMethodSelector = (state: { withdrawal?: WithdrawalSlice }) =>
  state.withdrawal?.currentMethod;

export const methodNameSelector = (state: { withdrawal?: WithdrawalSlice }) =>
  state.withdrawal?.currentMethod?.displayName || '';

export const isSelfTargetSelector = (state: { withdrawal?: WithdrawalSlice }) =>
  Boolean(state?.withdrawal?.currentMethod?.isSelfTarget);

export const extendedTypeForCardParamSelector = (state: { withdrawal?: WithdrawalSlice }) =>
  state?.withdrawal?.extendedTypeForCardParam;

export const currentWithdrawalMethodCurrencySelector = (state: { withdrawal?: WithdrawalSlice }) =>
  state?.withdrawal?.currentMethod?.currencyAlpha3 ||
  getCachedGroupAttributes().currencyAttr ||
  DEFAULT_EMPTY_CURRENCY_ALPHA3;

export const currentWithdrawalCurrencyPrecisionSelector = (state: { withdrawal?: WithdrawalSlice }) =>
  state?.withdrawal?.currentMethod?.currencyPrecision ?? DEFAULT_EMPTY_CURRENCY_PRECISION;

export const currentBetShopSelector = (state: { withdrawal?: WithdrawalSlice }) => state.withdrawal?.selectedBetShop;

export const betShopOptionsSelector = (state: { withdrawal?: WithdrawalSlice }) =>
  state.withdrawal?.betShopOptions || [];

export const currencyDisplaySelector = (state: { withdrawal: WithdrawalSlice }) =>
  state.withdrawal.widgetSettings.currency_display_setting_id;

export const kycCardVerificationSelector = (state: { withdrawal: WithdrawalSlice }) =>
  state.withdrawal.widgetSettings?.kyc_card_verification || false;

export const getProcessingPageRedirectTimeoutPayout = (state: { withdrawal: WithdrawalSlice }) =>
  state.withdrawal.widgetSettings.processing_page_redirect_timeout;

export const betShopSetValueCallbackSelector = ({ withdrawal }: { withdrawal: WithdrawalSlice }) =>
  withdrawal?.betShopSetValueCallback;

export const betShopFieldNameSelector = ({ withdrawal }: { withdrawal: WithdrawalSlice }) =>
  withdrawal?.betShopFieldName || '';

export const payoutGroupCategoriesSelector = (state: { withdrawal?: WithdrawalSlice }) =>
  state.withdrawal?.paygroupCategories;

export const showAllMethodsWithdrawalSelector = (state: { withdrawal?: WithdrawalSlice }) =>
  Boolean(
    state.withdrawal?.paygroupCategories?.is_category_scroll_locked &&
    state.withdrawal?.paygroupCategories?.is_in_carousel,
  );

export const withdrawalMethodsSelector = (state: { withdrawal?: WithdrawalSlice }) =>
  state.withdrawal?.withdrawalMethods || [];

export const shouldSkipInitFeatureSelector = (state: { withdrawal?: WithdrawalSlice }) =>
  state.withdrawal?.shouldSkipInitFeature || false;

export const {
  setWithdrawalTax,
  setCurrentMethod,
  setWithdrawalCardsList,
  setSelectedWithdrawalCard,
  disableAccountVerification,
  setAccVarificationData,
  cleanCurrentMethod,
  setVerifyNewPayoutAccount,
  setExtendedTypeForCardParam,
  setBetShopSelected,
  setBetShopOptions,
  setWidgetSettings,
  setIsNewPamAccount,
  setBetShopFieldName,
  setBetShopSetValueCallback,
  setPayoutGroupCategories,
  setWithdrawalMethods,
  setShouldSkipInitFeature,
  setAmountStepConfig,
} = withdrawalSlice.actions;

export const withdrawalAmountCanBeChangedSelector = (state: { withdrawal?: WithdrawalSlice }) =>
  state.withdrawal?.currentMethod?.amountCanBeChanged || false;

export const withdrawalPossibleChangesAmountSelector = (state: { withdrawal?: WithdrawalSlice }): number =>
  state.withdrawal?.currentMethod?.possibleChangesAmount || 0;

export default withdrawalSlice.reducer;
